import { useMemo, useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import SimulationCard from "@/components/SimulationCard";
import SimulationPreview from "@/components/SimulationPreview";
import { CATEGORIES, SIMULATIONS, categoryMeta, findSimulation } from "@/data/simulations";
import { track } from "@/lib/analytics";
import { readStoredSession, clearStoredSession } from "@/simulations/someone-i-met-online/attemptClient";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const ACCENT = "#4FA3D9";
const DEV_PREVIEW = process.env.NODE_ENV === "development";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className="shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors"
    style={
      active
        ? { borderColor: ACCENT, color: ACCENT, background: "rgba(79,163,217,0.08)" }
        : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }
    }
  >
    {children}
  </button>
);

export default function SimulationsLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [params] = useSearchParams();

  // slug is provided ONLY by the preview route (/simulations/:slug/preview).
  const isPreviewRoute = !!slug;

  // Category resolution:
  //  - clean permanent URL:  /simulations/<category-slug>
  //  - legacy / preview:     ?category=<category-slug>
  const segs = location.pathname.split("/").filter(Boolean);
  const pathCategory =
    !isPreviewRoute && segs.length === 2 && segs[0] === "simulations" && categoryMeta(segs[1])
      ? segs[1]
      : "";
  const queryCategoryRaw = params.get("category") || "";
  const validQueryCategory = queryCategoryRaw && categoryMeta(queryCategoryRaw) ? queryCategoryRaw : "";
  const active = pathCategory || validQueryCategory;
  const activeMeta = active ? categoryMeta(active) : null;

  // Legacy ?category= on the bare /simulations page → normalise to the clean
  // permanent category URL (preserving any UTM/campaign params). No loop: the
  // clean path carries no ?category, so this fires once.
  useEffect(() => {
    if (isPreviewRoute) return;
    if (segs.length === 1 && segs[0] === "simulations" && validQueryCategory) {
      const next = new URLSearchParams(params);
      next.delete("category");
      const q = next.toString();
      navigate(`/simulations/${validQueryCategory}${q ? `?${q}` : ""}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, validQueryCategory]);

  const setCategory = (catSlug) => navigate(catSlug ? `/simulations/${catSlug}` : "/simulations");
  // Public: available simulations navigate to their public route. Development builds only:
  // a coming-soon simulation previewed for QA starts via the existing /dev/simulation/:slug entry.
  // Sim #2 only: contextual card action from the existing sessionStorage pointer (no backend call, no new storage).
  // Pointer present = an incomplete attempt (normal completion and safeguarding Close clear it).
  const [resumeSid, setResumeSid] = useState(() => readStoredSession());
  const [startOverSim, setStartOverSim] = useState(null);
  const resumableFor = (sim) => (sim.simulationId === "sim_000002" && sim.status === "available" ? !!resumeSid : false);
  const confirmStartOver = () => {
    if (!startOverSim) return;
    clearStoredSession(); // only adaptience_sim2_session; the old attempt stays server-side
    setResumeSid(null);
    const route = startOverSim.route;
    setStartOverSim(null);
    navigate(route);
  };

  const onPractice = (sim) => {
    if (sim.status === "available" && sim.route) return navigate(sim.route);
    if (DEV_PREVIEW && sim.status !== "available") return navigate(`/dev/simulation/${sim.slug}`);
  };
  const openPreview = (sim) => {
    if (!sim.previewRoute) return;
    navigate(`${sim.previewRoute}${active ? `?category=${active}` : ""}`);
  };
  const closePreview = () => {
    if (slug) track("simulation_preview_close", slug);
    navigate(active ? `/simulations/${active}` : "/simulations");
  };

  // Preview is open when the URL carries a valid, available, previewable slug
  // (development builds may also preview coming-soon simulations for QA).
  const previewSim = useMemo(() => {
    if (!slug) return null;
    const s = findSimulation(slug);
    return s && s.previewRoute && (s.status === "available" || DEV_PREVIEW) ? s : null;
  }, [slug]);

  // Filter by category, then surface available simulations before coming-soon ones.
  const sims = useMemo(() => {
    const list = active ? SIMULATIONS.filter((s) => s.category === active) : SIMULATIONS;
    const rank = (s) => (s.status === "available" ? 0 : 1);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [active]);

  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="simulations-page">
      {isPreviewRoute ? (
        <Seo
          title={(previewSim && previewSim.seoTitle) || "Interactive Life Simulations for Real-World Situations | Adaptience"}
          description={(previewSim && previewSim.seoDescription) || "Explore interactive life simulations for practising decisions, relationships, safety, change, setbacks and everyday real-world situations."}
          canonical={previewSim ? `/simulations/${previewSim.slug}` : "/simulations"}
          robots="noindex, follow"
          ogTitle={previewSim ? previewSim.ogTitle : undefined}
          ogDescription={previewSim ? previewSim.ogDescription : undefined}
          ogImage={previewSim ? previewSim.ogImage : undefined}
          ogUrl={previewSim ? `/simulations/${previewSim.slug}/preview` : undefined}
        />
      ) : activeMeta ? (
        <Seo
          title={activeMeta.seoTitle}
          description={activeMeta.seoDescription}
          canonical={`/simulations/${active}`}
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://adaptience.com/" },
                { "@type": "ListItem", position: 2, name: "Simulations", item: "https://adaptience.com/simulations" },
                { "@type": "ListItem", position: 3, name: activeMeta.name, item: `https://adaptience.com/simulations/${active}` },
              ],
            },
          ]}
        />
      ) : (
        <Seo
          title="Interactive Life Simulations for Real-World Situations | Adaptience"
          description="Explore interactive life simulations for practising decisions, relationships, safety, change, setbacks and everyday real-world situations."
          canonical="/simulations"
        />
      )}
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>
      <Navbar />

      <main className="relative z-10 mx-auto w-full max-w-6xl px-6 py-14 sm:px-10 sm:py-20">
        {/* HERO */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="library-hero">
          <h1 className="font-display text-4xl font-semibold uppercase leading-[1.02] tracking-tight sm:text-6xl">
            Explore Simulations
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/60 sm:text-xl">
            Practice real-life situations before the stakes are real.
          </p>
        </motion.div>

        {/* CATEGORY FILTER — scrollable rail on mobile, wraps on larger screens */}
        <div
          className="mt-12 -mx-6 flex flex-nowrap gap-2.5 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          data-testid="category-filter"
        >
          <Chip active={!active} onClick={() => setCategory("")} testid="cat-all">All</Chip>
          {CATEGORIES.map((c) => (
            <Chip key={c.slug} active={active === c.slug} onClick={() => setCategory(c.slug)} testid={`cat-${c.slug}`}>
              {c.name}
            </Chip>
          ))}
        </div>

        {/* active category description */}
        {activeMeta && (
          <p className="mt-6 max-w-2xl border-l-2 pl-4 text-sm leading-relaxed text-white/65" style={{ borderColor: ACCENT }} data-testid="category-description">
            {activeMeta.description}
          </p>
        )}

        {/* GRID */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="sims-grid">
          {sims.length === 0 ? (
            <p className="text-white/60" data-testid="sims-empty">
              We're building simulations for this area. Check back soon.
            </p>
          ) : (
            sims.map((s) => <SimulationCard key={s.id} sim={s} onPreview={openPreview} resumable={resumableFor(s)} onStartOver={() => setStartOverSim(s)} />)
          )}
        </div>

        {/* SUGGEST A SITUATION — restrained, secondary to PRACTICE */}
        <section
          className="mt-20 rounded-2xl border border-white/[0.08] bg-white/[0.02] px-6 py-12 text-center sm:px-10 sm:py-14"
          data-testid="suggest-situation"
        >
          <h2 className="mx-auto max-w-xl font-display text-2xl font-medium leading-snug tracking-tight text-white sm:text-3xl">
            Don't see a situation you'd like to practise?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/55 sm:text-base">
            Tell us what you'd like to see next. Your ideas help shape what we build.
          </p>
          <Link
            to="/contact"
            data-testid="suggest-situation-btn"
            className="group mt-8 inline-flex items-center gap-2.5 rounded-full border px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] transition-[color,background-color] duration-500 active:scale-[0.98]"
            style={{ borderColor: ACCENT, color: ACCENT }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = ACCENT; }}
          >
            Suggest a Situation
            <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </section>

        <div className="mt-24">
          <SiteFooter />
        </div>
      </main>

      <SimulationPreview sim={previewSim} onClose={closePreview} onPractice={onPractice} />

      <AlertDialog open={!!startOverSim} onOpenChange={(o) => { if (!o) setStartOverSim(null); }}>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] rounded-2xl border border-white/12 bg-[#0d1217] p-6 text-white sm:max-w-sm" data-testid="start-over-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl font-semibold tracking-tight text-white" data-testid="start-over-title">Start over?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-white/65" data-testid="start-over-description">
              Your current unfinished practice will no longer be resumable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="start-over-cancel" className="mt-0 rounded-full border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStartOver} data-testid="start-over-confirm" className="rounded-full text-white hover:opacity-90" style={{ background: ACCENT }}>Start Over</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
